import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import axiosInstance from "../../helpers/AxiosInstance";
import i18next from "../../helpers/i18n";
import ReactSecurityHelper from "../../helpers/ReactSecurityHelper";
import CookieHelper from "../../helpers/CookieHelper";
const initialState = {
  isLoggedIn: null,
  AuthedUser: null,

  companyData: false,
  courierData: null,
};
// ============================= Tool Kit ================================
export const handleCompanyCode = createAsyncThunk(
  "Auth/handleCompanyCode",
  async ({ code, setLoading, setStep }) => {
    const response = await axios.get("/meta/metadata.json");
    const data = response.data;
    const isMatched = data.ClientID === parseInt(code.trim());
    
    // send data by action.payload and trim it as founded in fulfilled processing
    if (isMatched) {
      setStep(2);
      setLoading(false);
      return data;
    }
  },
);
// =======================================================================
export const checkCompanyData = createAsyncThunk(
  "Auth/checkCompanyData",
  async () => {
    let res = await axios.get("/meta/metadata.json");
    return res.data;
  },
);
// =============================================================================
export const handleUserLogin = createAsyncThunk(
  "auth/handleUserLogin",
  async ({ data, setLoading }) => {
    const CompanyData = JSON.parse(localStorage.getItem("CompanyData"));
    const { APIHost, APIPath, ClientID } = CompanyData;
    const baseURL = `${APIHost}${APIPath}api/ClientUsers/V6`;
    const { username, password } = data;
    const { language } = i18next;
    setLoading(true);

    // For login, use direct axios call without AccessToken
    // No signature for login request since we don't have AccessToken yet
    const loginPath = `${baseURL}/Login`;
    
    const response = await axios.post(
      loginPath,
      {
        username,
        password,
      },
      {
        headers: {
          "Accept-Encoding": "gzip, deflate, br",
          ApplicationID: "131352",
          CompanyID: ClientID,
          Language: language,
        },
        withCredentials: true, // Enable cookies for login
      },
    );
    return response.data;
  },
);
// ==========================================================================
export const handleLogout = createAsyncThunk("Auth/handleLogout", async () => {
  const instance = axiosInstance();
  const response = await instance.post("/Logout");
  return response;
});
// ============================================================================
const authSlice = createSlice({
  name: "Auth",
  initialState,
  reducers: {
    //actions
    checkUser: (state) => {
      const AuthedUser = localStorage.getItem("AuthedUser");
      if (AuthedUser) {
        try {
          const authedUserData = JSON.parse(AuthedUser);
          // Check if the user data has AccessToken
          if (authedUserData && authedUserData.AccessToken) {
            state.AuthedUser = authedUserData;
            state.isLoggedIn = true;
          } else {
            // Invalid user data, remove it
            localStorage.removeItem("AuthedUser");
            state.isLoggedIn = false;
            state.AuthedUser = null;
          }
        } catch (error) {
          // Invalid JSON, remove it
          localStorage.removeItem("AuthedUser");
          state.isLoggedIn = false;
          state.AuthedUser = null;
        }
      } else {
        state.isLoggedIn = false;
        state.AuthedUser = null;
      }
    },
    logOut: (state) => {
      state.AuthedUser = null;
      state.isLoggedIn = false;
      // Clear authentication cookies
      CookieHelper.clearAuthCookies();
    },
    checkCompany: (state, action) => {
      const companyData = localStorage.getItem("CompanyData");
      if (companyData) {
        state.companyData = JSON.parse(companyData);
        action.payload.setStep(2);
      } else {
        action.payload.setStep(1);
      }
    },
  },
  extraReducers: {
    [handleUserLogin.pending]: (state) => {
      state.loading = true;
    },
    [handleUserLogin.fulfilled]: (state, action) => {
      state.loading = false;
      // Validate that the response contains AccessToken
      if (action.payload && action.payload.AccessToken) {
        state.AuthedUser = action.payload;
        localStorage.setItem("AuthedUser", JSON.stringify(action.payload));
        
        // Set authentication cookies
        CookieHelper.setAuthCookies(action.payload);
        
        state.isLoggedIn = true;
      } else {
        // Invalid login response, treat as error
        state.loading = false;
        state.AuthedUser = null;
        state.isLoggedIn = false;
        console.error("Login response missing AccessToken:", action.payload);
      }
    },
    [handleUserLogin.rejected]: (state) => {
      state.loading = false;
    },
    [handleCompanyCode.pending]: (state) => {
      state.loading = true;
    },
    [handleCompanyCode.fulfilled]: (state, action) => {
      if (typeof action.payload == "undefined") {
        state.companyData = false;
      } else {
        state.loading = false;
        state.companyData = action.payload;
        localStorage.setItem("CompanyData", JSON.stringify(action.payload));
      }
    },
    [handleCompanyCode.rejected]: (state) => {
      state.loading = false;
    },
    [handleLogout.pending]: (state, action) => {
      state.loading = true;
    },
    [handleLogout.fulfilled]: (state, action) => {
      state.loading = false;
      state.AuthedUser = action.payload;
      localStorage.removeItem("AuthedUser");
      CookieHelper.clearAuthCookies(); // Clear cookies on logout
      state.isLoggedIn = false;
    },
    [handleLogout.rejected]: (state, action) => {
      state.loading = false;
      localStorage.removeItem("AuthedUser");
      CookieHelper.clearAuthCookies(); // Clear cookies on logout error
    },
    [checkCompanyData.pending]: (state, action) => {
      state.loading = true;
    },
    [checkCompanyData.fulfilled]: (state, action) => {
      if (typeof action.payload === "string") {
        const companyData = localStorage.getItem("CompanyData");
        if (companyData) {
          let companyMetaData = JSON.parse(companyData);
          delete companyMetaData.defined;
          state.companyData = companyMetaData;
        } else {
          if (action.payload.setStep) {
            action.payload.setStep(1);
          }
        }
      } else {
        const metaData = { ...action.payload, defined: true };
        localStorage.removeItem("CompanyData");
        localStorage.setItem("CompanyData", JSON.stringify(metaData));
        state.companyData = metaData;
      }
    },
    [checkCompanyData.rejected]: (state, action) => {
      const companyData = localStorage.getItem("CompanyData");
      if (companyData) {
        const companyMetaData = JSON.parse(companyData);
        delete companyMetaData.defined;
        state.companyData = companyMetaData;
      }
    },
  },
});

export const { checkCompany, checkUser, logOut } = authSlice.actions;
export default authSlice.reducer;
